// src/pages/view-profile/Reviews.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { ArrowLeft, Star, User, Calendar, Package } from "lucide-react";
import { userRepository } from "../../repositories/userRepository";
import { ratingsRepository } from "../../repositories/ratingsRepository";
import { errorDev } from '../../utils/logger.js';

export default function Reviews() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [ratingStats, setRatingStats] = useState({ avg: 0, count: 0 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const profileDoc = await userRepository.getById(userId);
        if (profileDoc) setProfile(profileDoc);

        // One canonical ratings read. rows[].raterId is normalized from the
        // legacy fromUserId alias, so reviewer lookup needs no field guessing.
        const baseReviews = await ratingsRepository.listForTarget(userId);

        // Deduplicate reviewer reads to keep Firestore usage efficient.
        const reviewerIds = [...new Set(baseReviews.map((r) => r.raterId).filter(Boolean))];
        const reviewerRows = await userRepository.getByIds(reviewerIds);
        const reviewerById = Object.fromEntries(reviewerRows.map((row) => [row.id, row]));

        const reviewsData = baseReviews.map((review) => ({
          ...review,
          reviewerProfile: reviewerById[review.raterId] || null,
        }));

        // Calculate rating stats
        if (reviewsData.length > 0) {
          const total = reviewsData.reduce((sum, review) => sum + (review.rating || 0), 0);
          const avg = total / reviewsData.length;
          setRatingStats({ avg, count: reviewsData.length });
        }

        setReviews(reviewsData);
      } catch (err) {
        // Keep UI stable even if some data fails.
        errorDev("Reviews: failed to load reviews", err);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={`${
          i < rating
            ? "fill-amber-400 text-amber-400"
            : "text-gray-300 dark:text-gray-600"
        }`}
      />
    ));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      default:
        return <Package size={14} className="text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading reviews..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-neutral-950 dark:via-neutral-950 dark:to-neutral-900">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5 sm:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mt-1 p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-xl transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-white truncate">
              Reviews for {profile?.name || "User"}
            </h1>
            <div className="mt-3 rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/80 backdrop-blur px-3 sm:px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Average rating</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white">{ratingStats.avg.toFixed(1)}</span>
                    <div className="flex">{renderStars(Math.round(ratingStats.avg))}</div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {ratingStats.count} {ratingStats.count === 1 ? 'review' : 'reviews'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-3 sm:space-y-4">
          {reviews.length === 0 ? (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 text-center py-12">
              <Star className="w-14 h-14 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                No Reviews Yet
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 px-4">
                This user hasn't received any reviews yet.
              </p>
            </div>
          ) : (
            reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4 sm:p-5"
              >
                {/* Review Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-200 dark:bg-neutral-700 rounded-full overflow-hidden shrink-0">
                      {review.reviewerProfile?.avatar ? (
                        <img
                          src={review.reviewerProfile.avatar}
                          alt={review.reviewerProfile.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-neutral-600 text-gray-500 dark:text-gray-400">
                          <User size={16} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {review.reviewerProfile?.name || "Anonymous User"}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <div className="flex">
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(review.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {review.transactionType && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-neutral-700 rounded text-xs text-gray-600 dark:text-gray-400">
                      {getTransactionIcon(review.transactionType)}
                      <span className="capitalize">{review.transactionType}</span>
                    </div>
                  )}
                </div>

                {/* Review Comment */}
                {review.comment && (
                  <p className="text-sm sm:text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed">
                    {review.comment}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}